import { useTranslation } from 'react-i18next';
import React from 'react';

import { GlacierVideoExploration } from '../../components/BrandHomePage/MeaningfulExplorationSection/GlacierVideoExploration/GlacierVideoExploration';
import ImageTextPaginated from '../../components/shared/ImageTextPaginated/ImageTextPaginated';
import HorizontalCarousel from '../../components/HorizontalCarousel/HorizontalCarousel';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { TeamMembersCarousel } from '../../components/TeamMembersCarousel';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import Footer from '../../components/Footer/Footer';

import { images } from '../../components/shared/assets';

import './OurStoryPage.css';

const OurStoryPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="our-story-page">


        <title>{t('meta:ourStory.title')}</title>
        <meta name="description" content={t('meta:ourStory.description')} />
        <meta property="og:title" content={t('meta:ourStory.title')} />
        <meta property="og:description" content={t('meta:ourStory.description')} />


      {/* Navigation */}
      <SmartNavigation
        defaultVariant="transparent"
        scrolledVariant="light"
        autoSwitchOnScroll={true}
        forceWhiteAssets={true}
      />

      {/* Timeline Carousel Section - Horizontal scrolling like Figma design */}
      <HorizontalCarousel fixTop={true}>

        {/* Opening Diptych - Text + Image */}
        <div className="timeline-intro">
          <div className="timeline-intro-left">
            <div className="timeline-lonly-text">
              {t('pages/ourStory:timeline.intro')}
            </div>
          </div>
          <div className="timeline-intro-right">
            <img src={images.ourStoryTimelineHero} alt="Awasi landscape" />
          </div>
        </div>


        {/* 2010 - Calendar Text Block */}
        <div className="timeline-calendar">
          <div className="timeline-calendar-year">2010</div>
          <div className="timeline-calendar-content">
            <h3 className='timeline-calendar-title'>{t('pages/ourStory:timeline.2010.title')}</h3>
            <p className='timeline-calendar-text'>{t('pages/ourStory:timeline.2010.content')}</p>
          </div>
        </div>

        {/* Video Section */}
        <div className="timeline-fullwidth">
          <img src={images.ourStoryVideoBackground} alt="timeline video background" />
        </div>

        {/* Image Text Blurb Section */}
        <div className="timeline-info">
          <div className="timeline-info-left">
            <div className="timeline-info-image"><img src={images.ourStoryAtacamaImage} alt="Awasi story" /></div>
            <div className="timeline-info-text">{t('pages/ourStory:timeline.story.origin')}</div>
          </div>
          <div className="timeline-info-right">{t('pages/ourStory:timeline.story.philosophy')}</div>
        </div>

        {/* 2020 - Calendar Text Block */}
        <div className="timeline-calendar">
          <div className="timeline-calendar-year">2020</div>
          <div className="timeline-calendar-content">
            <h3 className='timeline-calendar-title'>{t('pages/ourStory:timeline.2020.title')}</h3>
            <p className='timeline-calendar-text'>{t('pages/ourStory:timeline.2020.content')}</p>
          </div>
        </div>

        {/* Collage Section */}
        <div className="timeline-collage">
          <div className="timeline-collage-image timeline-collage-image-1">
            <img
              src={images.ourStoryCollage1}
              alt="Collage 1"
            />
          </div>
          <div className="timeline-collage-image timeline-collage-image-2">
            <img
              src={images.ourStoryCollage2}
              alt="Collage 2"
            />
          </div>
          <div className="timeline-collage-image timeline-collage-image-3">
            <img
              src={images.ourStoryCollage3}
              alt="Collage 3"
            />
          </div>
        </div>

        {/* Large Image */}
        <div className="timeline-fullwidth">
          <img src={images.ourStoryLargeLandscape} alt="Awasi landscape" />
        </div>

        {/* 2025 - Calendar Text Block */}
        <div className="timeline-calendar">
          <div className="timeline-calendar-year">2025</div>
          <div className="timeline-calendar-content">
            <h3 className='timeline-calendar-title'>{t('pages/ourStory:timeline.2025.title')}</h3>
            <p className='timeline-calendar-text'>{t('pages/ourStory:timeline.2025.content')}</p>
          </div>
        </div>
      </HorizontalCarousel>
      <div className="content-wrapper">


        {/* Meaningful Exploration Carousel Section */}
        <div data-scroll-section="exploration" className="meaningful-exploration-wrapper scroll-reveal-section">
          <GlacierVideoExploration />
        </div>

        {/* Full Viewport Waterfall Video Section */}
        <div data-scroll-section="waterfall-video" className="waterfall-video-section scroll-reveal-section">
          <div className="waterfall-video-background">
            <video
              className="waterfall-video-element"
              autoPlay
              loop
              muted
              playsInline
            >
              <source src="https://videos.ctfassets.net/hz1mviiqoqof/4J725DRkkj6GCsUPRltKxm/57a2b61783834fb53cd438548b3e5b84/team_awasi_video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {/* Optional mute button overlay */}
            <button className="video-mute-button">
              <div className="mute-icon">
                <img src={images.ourStoryMuteIcon} alt="Mute" />
              </div>
            </button>
          </div>
        </div>

        {/* Team & Talents Section - Horizontal Scroll Carousel */}
        <div data-scroll-section="team-talents" className="scroll-reveal-section">
          <TeamMembersCarousel />
        </div>

        {/* Equality, Diversity & Inclusion Section - Using CarbonNeutral Pattern */}
        <div data-scroll-section="equality" className="equality-diversity-section scroll-reveal-section">
          <ImageTextPaginated
            backgroundColor="#7B6569"
            foregroundColor="#F3F3F3"
            heading={t('pages/ourStory:equality.title')}
            slides={[
              {
                heading: t('pages/ourStory:equality.inclusion.title'),
                description: t('pages/ourStory:equality.inclusion.description'),
                linkUrl: "/atacama/dining",
                linkLabel: t('pages/ourStory:readMore'),
                videoUrl: "https://videos.ctfassets.net/hz1mviiqoqof/6dh3iAO2quzL3vlJAE3lK8/e74360823de0f7d4f5f862a7a6a194ba/team_member_video.mp4"
              },
              {
                heading: t('pages/ourStory:equality.internship.title'),
                description: t('pages/ourStory:equality.internship.description'),
                linkUrl: "/atacama/dining",
                linkLabel: t('pages/ourStory:readMore'),
                videoUrl: "https://videos.ctfassets.net/hz1mviiqoqof/6dh3iAO2quzL3vlJAE3lK8/e74360823de0f7d4f5f862a7a6a194ba/team_member_video.mp4"
              },
              {
                heading: t('pages/ourStory:equality.workshops.title'),
                description: t('pages/ourStory:equality.workshops.description'),
                linkUrl: "/atacama/dining",
                linkLabel: t('pages/ourStory:readMore'),
                videoUrl: "https://videos.ctfassets.net/hz1mviiqoqof/6dh3iAO2quzL3vlJAE3lK8/e74360823de0f7d4f5f862a7a6a194ba/team_member_video.mp4"
              },
              {
                heading: t('pages/ourStory:equality.workingTogether.title'),
                description: t('pages/ourStory:equality.workingTogether.description'),
                linkUrl: "/atacama/dining",
                linkLabel: t('pages/ourStory:readMore'),
                videoUrl: "https://videos.ctfassets.net/hz1mviiqoqof/6dh3iAO2quzL3vlJAE3lK8/e74360823de0f7d4f5f862a7a6a194ba/team_member_video.mp4"
              },
            ]}
          />
        </div>

        {/* Press & Awards Section - Figma Exact Design */}
        <div data-scroll-section="awards" className="press-awards-section scroll-reveal-section">
          <div className="press-awards-container">
            <h2 className="press-awards-title">{t('pages/ourStory:awards.title')}</h2>

            <div className="press-awards-list">
              <div className="press-award-item">
                <div className="award-text">{t('pages/ourStory:awards.list.telegraph')}</div>
                <div className="award-properties">{t('pages/ourStory:awards.list.telegraphProperties')}</div>
              </div>

              <div className="press-award-item">
                <div className="award-text">{t('pages/ourStory:awards.list.condeNast')}</div>
                <div className="award-properties">{t('pages/ourStory:awards.list.condeNastProperties')}</div>
              </div>

              <div className="press-award-item">
                <div className="award-text">{t('pages/ourStory:awards.list.travelLeisure')}</div>
                <div className="award-properties">{t('pages/ourStory:awards.list.travelLeisureProperties')}</div>
              </div>

              <div className="press-award-item">
                <div className="award-text">{t('pages/ourStory:awards.list.hollywood')}</div>
                <div className="award-properties">{t('pages/ourStory:awards.list.hollywoodProperties')}</div>
              </div>

              <div className="press-award-item">
                <div className="award-text">{t('pages/ourStory:awards.list.tripadvisor')}</div>
                <div className="award-properties">{t('pages/ourStory:awards.list.tripadvisorProperties')}</div>
              </div>

              <div className="press-award-item">
                <div className="award-text">{t('pages/ourStory:awards.list.wineSpectator')}</div>
                <div className="award-properties">{t('pages/ourStory:awards.list.wineSpectatorProperties')}</div>
              </div>
            </div>

            {/* <HandDrawnButton
              text={t('pages/ourStory:awards.seeMore')}
              onClick={() => navigate('/press-awards')}
              variant="brand"
              color="awasi"
              animation="stroke-to-stroke"
              size="normal"
              className="see-more-btn"
            /> */}
          </div>
        </div>


      </div>

      {/* Instagram Section with Our Story specific images */}
      <InstagramSection
        posts={[
          { src: images.ourStoryInstagram1, alt: "Patagonia mountain landscape" },
          { src: images.ourStoryInstagram2, alt: "Atacama desert landscape" },
          { src: images.ourStoryInstagram3, alt: "Natural hot spring" },
          { src: images.ourStoryInstagram4, alt: "Interior room with plants" }
        ]}
      />
      <Footer variant="default" />
    </div>
  );
};

export default OurStoryPage;